import React, { useEffect, useMemo, useState } from "react";
import { Add, Delete, Edit, Save } from "@mui/icons-material";
import { createProduct, deleteProduct, fetchAccounts, fetchProducts, updateProduct } from "../services/owdedApi";
import { money } from "./format";
import { EmptyState, Modal, StatusPill } from "./ui";
import { useAuth } from "../context/AuthContext";
import { canManageProducts } from "../utils/access";

const initialForm = {
  code: "",
  name: "",
  product_family: "SUNGA_BIZ",
  product_type_label: "Sunga Biz",
  currency: "CDF",
  cash_account_id: "",
  min_amount: 0,
  max_amount: 0,
  duration_value: 30,
  duration_unit: "DAY",
  commission_rate: 10,
  contribution_frequency: "DAILY",
  repayment_modality: "DAILY",
  disbursement_rule: "AFTER_MANAGER_VALIDATION",
  disbursement_weekday: "",
  status: "ACTIVE",
};

function ProductsPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [productRows, accountRows] = await Promise.all([fetchProducts(), fetchAccounts().catch(() => [])]);
    setProducts(productRows);
    setAccounts(accountRows);
  };

  useEffect(() => {
    load().catch(() => setNotice("Impossible de charger les produits."));
  }, []);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return products.filter((product) =>
      [product.code, product.name, product.product_family, product.product_type_label, product.currency, product.cash_account_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [products, search]);
  const cashAccounts = useMemo(
    () => accounts.filter((account) => account.account_type === "CASH" && account.currency === form.currency),
    [accounts, form.currency]
  );
  const canEditProducts = canManageProducts(user);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({ ...initialForm, ...product, cash_account_id: product.cash_account_id ? String(product.cash_account_id) : "" });
    setModalOpen(true);
  };

  const update = (field, value) => {
    setForm((current) => {
      const next = { ...current, [field]: value };
      if (field === "product_family" && value === "BOOTSTRAPPING") {
        next.duration_value = current.duration_value && Number(current.duration_value) > 0 ? current.duration_value : 12;
        next.duration_unit = "MONTH";
        next.contribution_frequency = "MONTHLY";
        next.repayment_modality = "MONTHLY";
        next.product_type_label = "Bootstrapping";
        next.disbursement_rule = "AFTER_MANAGER_VALIDATION";
        next.disbursement_weekday = "";
      }
      if (field === "product_family" && value === "SUNGA_BIZ") {
        next.duration_value = 30;
        next.duration_unit = "DAY";
        next.contribution_frequency = "DAILY";
        next.repayment_modality = "DAILY";
        next.product_type_label = "Sunga Biz";
        next.disbursement_rule = "AFTER_MANAGER_VALIDATION";
        next.disbursement_weekday = "";
      }
      if (field === "currency") {
        next.cash_account_id = "";
      }
      if (field === "duration_unit") {
        if (value === "MONTH") {
          next.duration_value = 12;
          next.contribution_frequency = "MONTHLY";
          next.repayment_modality = "MONTHLY";
        } else {
          next.duration_value = 30;
          next.contribution_frequency = "DAILY";
          next.repayment_modality = "DAILY";
        }
      }
      return next;
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        repayment_modality: form.contribution_frequency,
        disbursement_rule: "AFTER_MANAGER_VALIDATION",
        disbursement_weekday: null,
        cash_account_id: form.cash_account_id || null,
      };
      if (editing) {
        await updateProduct(editing.id, payload);
        setNotice("Produit modifie.");
      } else {
        await createProduct(payload);
        setNotice("Produit cree.");
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Impossible d'enregistrer le produit.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (product) => {
    if (!window.confirm(`Supprimer ${product.name} ?`)) return;
    try {
      await deleteProduct(product.id);
      setNotice("Produit supprime logiquement.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Suppression impossible.");
    }
  };

  return (
    <main className="page-content management-page">
      <div className="page-head">
        <div>
          <h1>Produits</h1>
          <p>Sunga Biz, Bootstrapping, plafonds, commissions et cycles de cotisation.</p>
        </div>
      </div>
      {notice && <p className="notice">{notice}</p>}

      <section className="panel data-panel">
        <div className="table-toolbar">
          <input placeholder="Recherche avancee..." value={search} onChange={(event) => setSearch(event.target.value)} />
          {canEditProducts && <button className="primary-btn" type="button" onClick={openCreate}><Add /> Ajouter</button>}
        </div>
        {filtered.length ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Libelle</th>
                <th>Type</th>
                <th>Compte</th>
                <th>Plafond</th>
                <th>Cotisation</th>
                <th>Commission</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((product) => (
                <tr key={product.id}>
                  <td>{product.code}</td>
                  <td>{product.name}</td>
                  <td>{product.product_type_label || product.product_family}</td>
                  <td>{product.cash_account_name || "Caisse principale auto"}<br /><small>{product.cash_account_id ? `#${product.cash_account_id}` : "-"}</small></td>
                  <td>{money(product.min_amount, product.currency)} - {money(product.max_amount, product.currency)}</td>
                  <td>{product.duration_value} {product.duration_unit} / {product.contribution_frequency}</td>
                  <td>{product.commission_rate}%</td>
                  <td><StatusPill value={product.status} /></td>
                  <td>
                    <div className="table-actions">
                      {canEditProducts && <button className="icon-action" type="button" onClick={() => openEdit(product)} aria-label="Modifier"><Edit /></button>}
                      {canEditProducts && <button className="icon-action danger" type="button" onClick={() => remove(product)} aria-label="Supprimer"><Delete /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <EmptyState />}
      </section>

      {modalOpen && (
        <Modal
          title={editing ? "Modifier le produit" : "Ajouter un produit"}
          onClose={() => setModalOpen(false)}
          footer={<button className="primary-btn" form="product-form" disabled={saving}><Save /> {saving ? "Enregistrement..." : "Enregistrer"}</button>}
        >
          <form id="product-form" onSubmit={submit}>
            <div className="form-grid">
              <label>ID produit<input value={form.code} onChange={(e) => update("code", e.target.value)} required /></label>
              <label>Libelle<input value={form.name} onChange={(e) => update("name", e.target.value)} required /></label>
              <label>Type<select value={form.product_family} onChange={(e) => update("product_family", e.target.value)}><option value="SUNGA_BIZ">Sunga Biz</option><option value="BOOTSTRAPPING">Bootstrapping</option><option value="OTHER">Autre</option></select></label>
              <label>Libelle du type<input value={form.product_type_label || ""} onChange={(e) => update("product_type_label", e.target.value)} placeholder="Ex : Fonds de roulement" /></label>
              <label>Devise<select value={form.currency} onChange={(e) => update("currency", e.target.value)}><option>CDF</option><option>USD</option></select></label>
              <label>Compte caisse lie<select value={form.cash_account_id || ""} onChange={(e) => update("cash_account_id", e.target.value)}><option value="">Caisse principale automatique</option>{cashAccounts.map((account) => <option key={account.id} value={account.id}>{account.name} - {money(account.current_balance, account.currency)}</option>)}</select></label>
              <label>Montant minimum<input type="number" value={form.min_amount} onChange={(e) => update("min_amount", e.target.value)} /></label>
              <label>Montant maximum<input type="number" value={form.max_amount} onChange={(e) => update("max_amount", e.target.value)} /></label>
              <label>Duree<input type="number" min="1" value={form.duration_value} readOnly={form.product_family === "SUNGA_BIZ"} onChange={(e) => update("duration_value", e.target.value)} /><span className="field-hint">{form.product_family === "BOOTSTRAPPING" ? "Duree modifiable en mois" : "Sunga Biz fixe a 30 jours"}</span></label>
              <label>Unite<select value={form.duration_unit} onChange={(e) => update("duration_unit", e.target.value)} disabled={form.product_family === "BOOTSTRAPPING"}><option value="DAY">Jour</option><option value="MONTH">Mois</option></select></label>
              <label>Cotisation<select value={form.contribution_frequency} disabled><option value="DAILY">Journalier</option><option value="WEEKLY">Hebdomadaire</option><option value="MONTHLY">Mensuel</option></select><span className="field-hint">{form.product_family === "BOOTSTRAPPING" ? `Mensuelle sur ${form.duration_value || 1} mois` : "Journaliere sur 30 jours"}</span></label>
              <label>Commission %<input type="number" value={form.commission_rate} onChange={(e) => update("commission_rate", e.target.value)} /><span className="field-hint">{form.product_family === "BOOTSTRAPPING" ? "Commission appliquee chaque mois" : "Commission prelevee au depart"}</span></label>
            </div>
          </form>
        </Modal>
      )}
    </main>
  );
}

export default ProductsPage;
